import { motion } from "framer-motion";
import { gsap } from "gsap";
import { useEffect, useRef, useState } from "react";
import { IoClose } from "react-icons/io5";
import { TiLocationArrow } from "react-icons/ti";
import GlassButton from "./ui/GlassButton";

const BookAConsultationCustomModal = ({ 
  isOpen, 
  onClose, 
  title = 'Consultation Form', 
  description = '', 
  image = '/assets/images/services/consultation.webp', 
}) => {
  const modalRef = useRef(null);
  const logoRef = useRef(null);
  const imageRef = useRef(null);
  const descriptionRef = useRef(null);
  const titleRef = useRef(null);

  // Local states for booking form
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [focusedField, setFocusedField] = useState(null);

  // Form submit handler
  const handleSubmitForm = (e) => {
    e.preventDefault();
    console.log("Booking Form Data:", {
      clientName,
      clientEmail,
      clientPhone,
      notes,
    });
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      // Animation for the entire modal
      gsap.fromTo(
        modalRef.current, 
        { scale: 0.8, opacity: 0, boxShadow: '0 0 0px rgba(64, 224, 208, 0)' }, 
        { 
          scale: 1, 
          opacity: 1, 
          boxShadow: '0 0 30px rgba(64, 224, 208, 0.6)', 
          duration: 0.8, 
          ease: "power3.out" 
        }
      );

      // Turquoise Glow pulse effect on the modal
      gsap.to(modalRef.current, { 
        boxShadow: '0 0 30px rgba(64, 224, 208, 0.8)', 
        repeat: -1, 
        yoyo: true, 
        duration: 2 
      });

      // Logo animation
      gsap.fromTo(
        logoRef.current, 
        { opacity: 0, scale: 0.8 }, 
        { 
          opacity: 1, 
          scale: 1, 
          boxShadow: '0 0 15px rgba(64, 224, 208, 0.8)', 
          duration: 0.8, 
          ease: "power2.out" 
        }
      );

      // In-and-out scale animation for the logo
      gsap.to(logoRef.current, { 
        scale: 1.1, 
        repeat: -1, 
        yoyo: true, 
        duration: 3, 
        ease: "power1.inOut" 
      });

      // Image animation
      gsap.fromTo(
        imageRef.current, 
        { opacity: 0, scale: 0.9 }, 
        { 
          opacity: 1, 
          scale: 1, 
          duration: 0.8, 
          ease: "power3.out", 
          delay: 0.4 
        }
      );

      // Description list animation (staggered)
      const descriptionItems = descriptionRef.current?.querySelectorAll("li");
      if (descriptionItems) {
        gsap.fromTo(
          descriptionItems, 
          { opacity: 0, x: -20 }, 
          { 
            opacity: 1, 
            x: 0, 
            stagger: 0.1, 
            duration: 0.6, 
            ease: "power3.out", 
            delay: 0.6 
          }
        );
      }

      // Title letter-by-letter drop-in animation
      const titleChars = titleRef.current?.querySelectorAll("span");
      if (titleChars) {
        gsap.fromTo(
          titleChars, 
          { opacity: 0, y: -50 }, 
          { 
            opacity: 1, 
            y: 0, 
            stagger: 0.05, 
            duration: 0.8, 
            ease: "power3.out" 
          }
        );
      }
    }
  }, [isOpen]);

  const handleClose = () => {
    // Portal zoom-out effect for the modal
    gsap.to(modalRef.current, {
      scale: 0.1, 
      opacity: 0, 
      duration: 0.6, 
      ease: "power3.inOut"
    });

    // Portal zoom-out effect for the title
    const titleChars = titleRef.current?.querySelectorAll("span");
    if (titleChars) {
      gsap.to(titleChars, { 
        scale: 0.1, 
        opacity: 0, 
        duration: 0.6, 
        ease: "power3.inOut" 
      });
    }

    // Call the onClose function after animation
    gsap.delayedCall(0.6, onClose);
  };

  if (!isOpen) return null;

  // Common input classes for Aceternity-style glassmorphic inputs
  const inputBaseClasses = "w-full px-4 py-3 rounded-xl bg-black/40 backdrop-blur-sm border text-white placeholder-gray-500 transition-all duration-300 focus:outline-none";
  const inputFocusClasses = "border-cyan-500/50 shadow-[0_0_15px_rgba(0,255,255,0.3)]";
  const inputNormalClasses = "border-white/10 hover:border-white/20";

  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex justify-center items-center z-[9999] transition-opacity duration-300 p-4"
      role="dialog" 
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div 
        className="relative bg-gradient-to-b from-gray-900/95 to-black/95 backdrop-blur-md text-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 md:p-10 border border-cyan-500/20"
        ref={modalRef}
      >
        
        {/* Spectacular X Close Button (Top Right) */}
        <motion.button
          onClick={handleClose}
          className="absolute top-4 right-4 z-50 w-10 h-10 flex items-center justify-center rounded-full bg-black/60 backdrop-blur-sm border border-cyan-500/30 text-cyan-400 cursor-pointer transition-all duration-300"
          whileHover={{ 
            scale: 1.15,
            boxShadow: "0 0 20px rgba(0, 255, 255, 0.8), 0 0 40px rgba(0, 255, 255, 0.4), 0 0 60px rgba(0, 255, 255, 0.2)"
          }}
          whileTap={{ scale: 0.95 }}
          aria-label="Close modal"
        >
          <IoClose size={22} />
        </motion.button>

        {/* ZeroBank Logo */}
        <div className="flex justify-center mb-4">
          <div className="w-24 h-24 p-2 border border-cyan-500/30 rounded-xl shadow-lg shadow-cyan-500/10 bg-black/60 backdrop-blur-sm flex items-center justify-center">
            <img 
              src="/assets/images/ZeroBankLogoShort.png" 
              alt="Zero Bank Logo" 
              className="w-16 h-auto rounded-lg" 
              ref={logoRef}
            />
          </div>
        </div>

        {/* Image Section */}
        {image && (
          <div className="mb-6 p-1 border border-cyan-500/20 rounded-xl overflow-hidden">
            <img 
              src={image} 
              alt={title} 
              className="w-full h-40 rounded-lg shadow-md object-cover" 
              ref={imageRef} 
            />
          </div>
        )}

        {/* Title */}
        <h2 
          id="modal-title" 
          className="text-2xl font-extrabold text-center mb-4 bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent"
          ref={titleRef}
        >
          {title.split('').map((char, index) => (
            <span key={index} className="inline-block">{char}</span>
          ))}
        </h2>

        {/* Description */}
        <div 
          className="text-white/80 text-sm md:text-base leading-5"
          ref={descriptionRef}
        >
          <ul className="list-disc list-inside space-y-2">
            {description.split('\n')
              .filter(point => point.trim() !== '')
              .map((point, index) => (
                <li key={index} className="flex items-start gap-2">
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    strokeWidth="2" 
                    stroke="#22d3ee" 
                    className="w-5 h-5 mt-[2px] flex-shrink-0"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{point}</span>
                </li>
            ))}
          </ul>
        </div>

        {/* Booking Form Section with Aceternity-style inputs */}
        <div className="mt-8">
          <h3 className="text-xl font-semibold mb-4 text-center">
            <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              Book Your Consultation
            </span>
          </h3>
          
          <form onSubmit={handleSubmitForm} className="space-y-5">
            {/* Name Field */}
            <div className="relative group">
              <label htmlFor="clientName" className="block text-sm font-medium mb-2 text-gray-300">
                Name <span className="text-cyan-400">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="clientName"
                  className={`${inputBaseClasses} ${focusedField === 'name' ? inputFocusClasses : inputNormalClasses}`}
                  placeholder="Your full name"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  onFocus={() => setFocusedField('name')}
                  onBlur={() => setFocusedField(null)}
                  required
                />
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-cyan-500/0 via-cyan-500/5 to-cyan-500/0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </div>

            {/* Email Field */}
            <div className="relative group">
              <label htmlFor="clientEmail" className="block text-sm font-medium mb-2 text-gray-300">
                Email <span className="text-cyan-400">*</span>
              </label>
              <div className="relative">
                <input
                  type="email"
                  id="clientEmail"
                  className={`${inputBaseClasses} ${focusedField === 'email' ? inputFocusClasses : inputNormalClasses}`}
                  placeholder="Your email address"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  required
                />
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-cyan-500/0 via-cyan-500/5 to-cyan-500/0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </div>

            {/* Phone Field */}
            <div className="relative group">
              <label htmlFor="clientPhone" className="block text-sm font-medium mb-2 text-gray-300">
                Phone <span className="text-gray-500 text-xs">(Optional)</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="clientPhone"
                  className={`${inputBaseClasses} ${focusedField === 'phone' ? inputFocusClasses : inputNormalClasses}`}
                  placeholder="Your phone number"
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                  onFocus={() => setFocusedField('phone')}
                  onBlur={() => setFocusedField(null)}
                />
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-cyan-500/0 via-cyan-500/5 to-cyan-500/0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </div>

            {/* Additional Notes */}
            <div className="relative group">
              <label htmlFor="notes" className="block text-sm font-medium mb-2 text-gray-300">
                Project Details
              </label>
              <div className="relative">
                <textarea
                  id="notes"
                  rows="4"
                  className={`${inputBaseClasses} resize-none ${focusedField === 'notes' ? inputFocusClasses : inputNormalClasses}`}
                  placeholder="Tell us about your project and requirements..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  onFocus={() => setFocusedField('notes')}
                  onBlur={() => setFocusedField(null)}
                />
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-cyan-500/0 via-cyan-500/5 to-cyan-500/0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </div>

            {/* Submit and Close Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
              <GlassButton
                type="submit"
                className="px-8 py-3"
                variant="cyan"
                size="lg"
              >
                <div className="flex items-center gap-2">
                  <TiLocationArrow className="text-xl" /> Submit Request
                </div>
              </GlassButton>
              
              <GlassButton
                onClick={handleClose}
                className="px-8 py-3"
                variant="gray"
                size="lg"
              >
                <div className="flex items-center gap-2">
                  <IoClose className="text-xl" /> Close
                </div>
              </GlassButton>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookAConsultationCustomModal;
